import { IconEditOrders } from 'assets/DesignSystem/SVGComponents';
import { CopyIcon } from 'assets/images/shopping';
import { NudosCareTag } from 'components/NudosComponents';
import {
  IProductDelivery,
  ITrackingInfo,
  IWarehouseTemporaryStorage,
  SHIPMENT_STATUS_ID
} from 'types/orders';
import { formatShortDateAndHour } from 'utils/formatDatesAndHours';
import { filterPillsToShow } from 'utils/pillsFormat';
import { copyTextToClipboard } from 'views/TrackingOrder/trackingOrder.utils';

interface ITrackingColumnDisplay {
  estimatedDate?: string;
  trackingUrl?: string;
  trackingCode?: string;
}

interface IShipmentProductRow {
  product: IProductDelivery;
  productNumber: number;
  totalProducts: number;
  status?: string;
  statusId?: number;
  trackingInfo?: ITrackingInfo;
  warehouseTemporaryStorage?: IWarehouseTemporaryStorage;
  onSerialClick?: () => void;
}

const ShipmentProductRow = ({
  product,
  productNumber,
  totalProducts,
  status,
  statusId,
  trackingInfo,
  warehouseTemporaryStorage,
  onSerialClick
}: IShipmentProductRow) => {
  const statusTextMap: Record<string, string> = {
    preparation: 'Preparación',
    confirmed: 'Confirmado',
    delivered: 'Entregado',
    in_transit: 'En tránsito',
    cancelled: 'Cancelado'
  };

  const statusClassMap: Record<string, string> = {
    preparation: 'preparation',
    confirmed: 'confirmed',
    delivered: 'delivered',
    in_transit: 'inTransit',
    cancelled: 'cancelled'
  };


  const getStatusText = (statusValue?: string): string => {
    if (!statusValue) return 'Sin estado';

    return statusTextMap[statusValue.toLowerCase()] || statusValue;
  };

  const getStatusClass = (statusValue?: string): string => {
    if (!statusValue) return 'default';

    return statusClassMap[statusValue.toLowerCase()] || 'default';
  };

  const toTrackingDisplayText = (value?: string | number | null): string | undefined => {
    if (value == null) return undefined;
    const text = String(value).trim();
    return text || undefined;
  };

  const getTransitToWarehouseTrackingDisplay = (
    wts?: IWarehouseTemporaryStorage
  ): ITrackingColumnDisplay => ({
    estimatedDate: toTrackingDisplayText(wts?.estimatedEntryDate),
    trackingUrl: toTrackingDisplayText(wts?.trackingUrl),
    trackingCode: toTrackingDisplayText(wts?.trackingCode)
  });
  
  const getDeliveryTrackingDisplay = (info?: ITrackingInfo): ITrackingColumnDisplay => ({
    estimatedDate: toTrackingDisplayText(info?.currentEstimatedDeliverDate),
    trackingUrl: toTrackingDisplayText(info?.trackingLink),
    trackingCode: toTrackingDisplayText(info?.trackerNumber)
  });

  const getTrackingColumnDisplay = (isTransitToWarehouse: boolean): ITrackingColumnDisplay => {
    if (isTransitToWarehouse) {
      return getTransitToWarehouseTrackingDisplay(warehouseTemporaryStorage);
    }

    return getDeliveryTrackingDisplay(trackingInfo);
  };

  const hasValidTrackingColumnDisplay = (display: ITrackingColumnDisplay): boolean =>
    Boolean(display.estimatedDate || display.trackingUrl || display.trackingCode);

  const specs = filterPillsToShow(product.pills).join(' - ');
  const statusText = getStatusText(status);
  const statusClass = getStatusClass(status);
  const hasSerial = Boolean(product.serial || (product.serials && product.serials.length > 0));
  const serialLinkText = hasSerial ? 'Ver/editar seriales' : 'Agregar serial';
  const isTransitToWarehouse = statusId === SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE;
  const trackingDisplay = getTrackingColumnDisplay(isTransitToWarehouse);
  const formattedDate = formatShortDateAndHour(trackingDisplay?.estimatedDate, -6)?.textWithTimezone;
  const showTrackingInfo = hasValidTrackingColumnDisplay(trackingDisplay);

  const handleSerialClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onSerialClick?.();
  };

  return (
    <div className="shipmentProductRow">
      <div className="productHeader">
        <span className="productCounter">
          Producto {productNumber} de {totalProducts}
        </span>
      </div>

      <div className="productContent">
        <div className="unitColumn">
          <span className="unitLabel">Und.</span>
          <span className="quantity">{product.unit || 1}</span>
        </div>

        <div className="productColumn">
          <span className="productLabel">Producto</span>
          <div className="productNameSection">
            <div className="productNameSectionContent">
              <span className="productName">{product.name || 'Sin nombre'}</span>
              {!!product?.nudosCareName && (
                <NudosCareTag
                  nudosCareType={product.nudosCareName}
                  customWidth="60px"
                  customHeight="18px"
                  customFontSize="10px"
                />
              )}
            </div>
            {specs && (
              <span className="productSpecs" title={specs}>
                {specs}
              </span>
            )}
            <button className="copyButton" onClick={() => copyTextToClipboard(specs)} aria-label="Copiar specs">
              <CopyIcon />
            </button>
          </div>
          <div className="skuSection">
            <span className="skuLabel">SKU Catálogo: {product.sku || 'Sin SKU'}</span>
            <button className="serialLink" onClick={handleSerialClick}>
              {serialLinkText} <IconEditOrders />
            </button>
          </div>
        </div>

        <div className="statusColumn">
          <span className="statusLabel">Estado</span>
          <span className={`statusBadge ${statusClass}`}>{statusText}</span>
        </div>

        <div className="trackingColumn">
          <span className="trackingLabel">Información de tracking</span>
          {showTrackingInfo ? (
            <div className="trackingContent">
              {formattedDate && <span className="trackingDate">{formattedDate}</span>}
              {trackingDisplay?.trackingUrl && (
                <span className="trackingLink">
                  Link de rastreo:{' '}
                  <a
                    href={trackingDisplay?.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="trackingLinkAnchor"
                  >
                    {trackingDisplay?.trackingUrl}
                  </a>
                </span>
              )}
              {trackingDisplay?.trackingCode && (
                <span className="trackerNumber">Código: {trackingDisplay?.trackingCode}</span>
              )}
            </div>
          ) : (
            <span className="noTrackingInfo" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ShipmentProductRow;
